import React, { useState } from "react";
import { Card, Typography, Input, Button, message } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";
import { Plane } from "lucide-react";
import axiosClient from "../api/axiosClient";

const { Title, Text } = Typography;

const VerifyPage: React.FC = () => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const navigate = useNavigate();

  const email = (location.state as any)?.email || undefined;

  const handleSubmit = async () => {
    if (!code || code.length !== 6) {
      messageApi.error("Please enter the 6-digit verification code");
      return;
    }
    try {
      setLoading(true);
      const res = await axiosClient.post("/verify", {
        email,
        code,
      });
      messageApi.success(res.data.message || "Email verified successfully!");
      navigate("/login");
    } catch (err: any) {
      messageApi.error(err.response?.data || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResending(true);
    try {
      await axiosClient.post("/resend-code", { email });
      messageApi.success("A new code has been sent.");
    } catch {
      messageApi.error("Failed to resend code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#f0f7ff 0%,#fff 50%,#faf5ff 100%)" }}>
        <div style={{ padding: 24 }}>
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, width: "fit-content" }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                transform: "rotate(45deg)",
              }}>
                <Plane size={22} />
              </div>
              <Title level={4} style={{ margin: 0, color: "inherit" }}>
                Hairplane
              </Title>
            </Link>

            <Card style={{ borderRadius: 16, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
              <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 320 }}>
                  <div style={{ textAlign: "center", marginBottom: 18 }}>
                    <div style={{ width: 64, height: 64, margin: "0 auto 12px", borderRadius: 14, background: "linear-gradient(135deg,#10b981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                      <LockOutlined />
                    </div>
                    <Title level={3}>Verify Your Account</Title>
                    <Text type="secondary">Please enter the 6-digit code sent to {email ? <strong>{email}</strong> : "your contact"}</Text>
                  </div>
                </div>

                <div style={{ flex: 2, minWidth: 320 }}>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    style={{ textAlign: "center", fontSize: 24, letterSpacing: 8, marginBottom: 16 }}
                    autoFocus
                  />

                  <Button type="primary" block size="large" loading={loading} onClick={handleSubmit}>
                    Verify
                  </Button>

                  <div style={{ marginTop: 12, textAlign: "center" }}>
                    <Button type="link" loading={resending} onClick={handleResend} disabled={!email}>
                      Didn't receive a code? Resend
                    </Button>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <Button type="link" onClick={() => navigate(-1)}>Edit contact</Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyPage;
